import React, { useState } from "react";
import { toast } from "react-toastify";
import { portalService } from "apiServices";
import { Buttons, Displays, Inputs } from "components";
import { BOOKING_SOURCE, LAST_NEW_FILE, RENDER_SEAT_BOOKING_ERROR } from "utils/functions/seatBooking";

// แอดมินจองให้: จ่ายเงิน (ผูกสมาชิกได้ สลิปไม่บังคับ แต่ไม่มีสลิปต้องมีหมายเหตุ) / ภายใน-VIP (ไม่คิดเงิน)
// ทั้งสองแบบอนุมัติทันที ไม่นับโควตาออนไลน์ และบันทึกว่าแอดมินคนไหนจอง
const AdminBookingModal = ({ activityId, seatIds, source, schools, onClose, onSuccess }) => {
  const isInternal = source === BOOKING_SOURCE.INTERNAL;
  const [_isBgLoading, _setIsBgLoading] = useState(false);
  const [_customer, _setCustomer] = useState(null);
  const [_booker, _setBooker] = useState({ first_name: "", last_name: "", phone: "", email: "" });
  const [_schoolCode, _setSchoolCode] = useState("GEN");
  const [_viewerNames, _setViewerNames] = useState({});
  const [_note, _setNote] = useState("");
  // แจ้งช่องที่ต้องกรอกใต้ input แต่ละช่อง (แบบเดียวกับฟอร์มอื่นใน portal)
  const [_errors, _setErrors] = useState({});
  const [_file, _setFile] = useState(null);

  // เลือกจาก autocomplete แล้วเติมข้อมูลผู้จองจากโปรไฟล์สมาชิก (แก้ต่อได้)
  const _selectCustomer = (customer) => {
    if (!customer || !customer._id) return;
    _setCustomer(customer);
    _setErrors((prev) => ({ ...prev, first_name: null }));
    _setBooker({
      first_name: customer.first_name || "",
      last_name: customer.last_name || "",
      phone: customer.phone_number || "",
      email: customer.email || "",
    });
  };

  const _setBookerField = (key) => (e) => {
    _setBooker((prev) => ({ ...prev, [key]: e.target.value }));
    _setErrors((prev) => ({ ...prev, [key]: null }));
  };

  const _handleSubmit = async () => {
    const errors = {};
    if (!_booker.first_name.trim()) {
      errors.first_name = isInternal ? "กรุณากรอกชื่อแขก / หน่วยงาน" : "กรุณากรอกชื่อผู้จอง";
    }
    if (!isInternal && !_file && !_note.trim()) {
      errors.note = "ไม่แนบสลิป ต้องกรอกหมายเหตุ (เช่น โอนรวม)";
    }
    _setErrors(errors);
    if (Object.keys(errors).length) return;
    const form = new FormData();
    form.append("booking_source", source);
    form.append("seat_ids", JSON.stringify(seatIds));
    form.append("booker", JSON.stringify(_booker));
    form.append(
      "seats",
      JSON.stringify(seatIds.map((seat_id) => ({ seat_id, viewer_name: _viewerNames[seat_id] || "" })))
    );
    form.append("school_code", _schoolCode);
    if (_customer) form.append("customer_id", _customer._id);
    if (_note.trim()) form.append("note", _note.trim());
    if (!isInternal && _file) form.append("image", _file);

    _setIsBgLoading(true);
    const res = await portalService.POST_SEAT_BOOKING_ADMIN_CREATE(activityId, form);
    _setIsBgLoading(false);
    if (res && res.status === 200) {
      toast.success(`จองสำเร็จ รหัสจอง ${res.data.code}`);
      onSuccess && onSuccess();
    } else {
      toast.error(RENDER_SEAT_BOOKING_ERROR(res, "จองไม่สำเร็จ"));
    }
  };

  return (
    <div className="modal_card">
      <Displays.BGLoading visible={_isBgLoading} />
      <div className="modal_title">
        {isInternal ? "จองภายใน / VIP (ไม่คิดเงิน)" : "จองให้ลูกค้า (จ่ายเงิน)"} · {seatIds.length} ที่นั่ง
      </div>
      <div className="hint" style={{ marginBottom: 12 }}>
        ที่นั่ง: {seatIds.join(", ")} · อนุมัติทันที และบันทึกว่าแอดมินจองให้
      </div>

      <div className="modal_row">
        <div className="m_label">ผูกกับบัญชีสมาชิก (ไม่บังคับ ถ้าผูก ลูกค้าจะเห็นตั๋วในแอป)</div>
        {_customer ? (
          <div className="inline_row">
            <div style={{ flex: 1 }}>
              {_customer.first_name} {_customer.last_name} · {_customer.phone_number} · {_customer.email}
            </div>
            <div className="w100">
              <Buttons.BgStandard theme_gray label="เอาออก" onClick={() => _setCustomer(null)} />
            </div>
          </div>
        ) : (
          <Inputs.InputCustomerAutoSuggestion
            apiService={portalService.GET_CUSTOMER_LIST}
            fieldRender={false}
            query={"?keyword"}
            placeholder="พิมพ์ชื่อ-นามสกุลสมาชิก แล้วเลือกจากรายการ"
            onChange={_selectCustomer}
          />
        )}
      </div>

      <div className="modal_row">
        <div className="m_label">{isInternal ? "ชื่อแขก / หน่วยงาน *" : "ชื่อผู้จอง *"}</div>
        <Inputs.TextField
          value={_booker.first_name}
          errors={_errors.first_name}
          onChange={_setBookerField("first_name")}
        />
      </div>
      {!isInternal && (
        <>
          <div className="modal_row">
            <div className="m_label">นามสกุล</div>
            <Inputs.TextField value={_booker.last_name} onChange={_setBookerField("last_name")} />
          </div>
          <div className="modal_row">
            <div className="m_label">เบอร์โทร</div>
            <Inputs.TextField value={_booker.phone} onChange={_setBookerField("phone")} />
          </div>
          <div className="modal_row">
            <div className="m_label">อีเมล</div>
            <Inputs.TextField value={_booker.email} onChange={_setBookerField("email")} />
          </div>
        </>
      )}
      {/* ทั้งแบบจ่ายเงินและภายใน/VIP เลือกโรงเรียนได้ ค่าเริ่มต้นไม่ระบุ เหมือนฝั่งลูกค้า */}
      <div className="modal_row">
        <div className="m_label">โรงเรียน</div>
        <Inputs.Dropdown
          value={_schoolCode}
          options={[
            { value: "GEN", label: "ไม่ระบุ" },
            ...schools.map((s) => ({ value: s.code, label: `${s.code} · ${s.name}` })),
          ]}
          onChange={_setSchoolCode}
        />
      </div>

      <div className="modal_row">
        <div className="m_label">ชื่อผู้ชมต่อที่นั่ง (ไม่บังคับ แสดงบนตั๋ว)</div>
        {seatIds.map((seatId) => (
          <div className="inline_row" key={seatId}>
            <div className="w100">{seatId}</div>
            <div style={{ flex: 1 }}>
              <Inputs.TextField
                value={_viewerNames[seatId] || ""}
                onChange={(e) => _setViewerNames((prev) => ({ ...prev, [seatId]: e.target.value }))}
              />
            </div>
          </div>
        ))}
      </div>

      {!isInternal && (
        <div className="modal_row">
          <div className="m_label">สลิป (ไม่บังคับ รูปหรือ PDF ไม่เกิน 8 MB)</div>
          <Inputs.UploadImage
            theme_upload_image
            acceptType="img-pdf"
            placeholder="เลือกไฟล์สลิป"
            isDelete
            onChange={(files) => {
              const file = LAST_NEW_FILE(files);
              if (file && file.size > 8 * 1024 * 1024) {
                toast.error("สลิปต้องไม่เกิน 8 MB");
                return;
              }
              _setFile(file);
              if (file) _setErrors((prev) => ({ ...prev, note: null }));
            }}
          />
        </div>
      )}
      <div className="modal_row">
        <div className="m_label">หมายเหตุ{!isInternal && !_file ? " * (ไม่มีสลิป เช่น โอนรวม)" : ""}</div>
        <Inputs.TextArea
          rows={3}
          value={_note}
          errors={_errors.note}
          onChange={(e) => {
            _setNote(e.target.value);
            _setErrors((prev) => ({ ...prev, note: null }));
          }}
        />
      </div>

      <div className="modal_btns">
        <div className="w120">
          <Buttons.BgStandard theme_gray label="ยกเลิก" onClick={onClose} />
        </div>
        <div className="w140">
          <Buttons.BgStandard theme_purple label="ยืนยันการจอง" onClick={_handleSubmit} />
        </div>
      </div>
    </div>
  );
};

export default AdminBookingModal;
